import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import Layout from '@src/components/Pages/Home/Layout/Layout';
import StudentLoanModal, {
  STUDENT_LOAN_MODAL_LOCATION_ID,
} from '@src/components/StudentLoan/StudentLoanModal/StudentLoanModal';
import useApplications from '@src/hooks/useApplications';
import { useMemberPlanContext } from '@src/context/MemberPlanContext';
import { useMemberTraitsContext } from '@src/context/MemberTraitsContext';
import { useModal } from '@src/context/ModalContext';
import LearningLifecycles from '@src/components/LearningLifecycles/LearningLifecycles';
import { getApplicationsOpenForEnrollment } from '@src/utilities/applicationUtility';
import EnrollmentConfirmationModal from '@src/components/Enrollment/EnrollmentConfirmationModal/EnrollmentConfirmationModal';
import MultipleEnrollmentConfirmationModal from '@src/components/Enrollment/MultipleEnrollmentConfirmationModal/MultipleEnrollmentConfirmationModal';
import ErrorBanner from '@shared/ErrorBanner/ErrorBanner';
import { Confetti } from '@src/components/Shared/Confetti/Confetti';
import { useConfetti } from '@src/context/ConfettiContext';
import Banner from '@src/components/Banner/Banner';
import './Home.scss';

const Home: React.FC = () => {
  const location = useLocation();
  const { memberPlan } = useMemberPlanContext();
  const { isModalOpen, openModal } = useModal();
  const { memberTraits } = useMemberTraitsContext();
  const { approvedApplications } = useApplications();
  const { showConfetti } = useConfetti();

  const displayApplicationsModal =
    !isModalOpen && memberTraits?.hasApprovedApplications && approvedApplications.length > 0;

  useEffect(() => {
    // let DataDog know this is 'usable' when all content is loaded
    if (memberPlan) {
      datadogRum.addTiming('home_page');

      // Automatically open the student loan modal
      if (
        location.hash === STUDENT_LOAN_MODAL_LOCATION_ID &&
        (memberPlan?.hasStudentLoanReimbursement || memberPlan?.hasStudentLoanOptimization)
      ) {
        openModal(<StudentLoanModal />);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberPlan]);

  useEffect(() => {
    if (displayApplicationsModal) {
      const enrollmentApplications = getApplicationsOpenForEnrollment(approvedApplications);
      if (enrollmentApplications?.length === 1) {
        openModal(
          <EnrollmentConfirmationModal
            application={enrollmentApplications[0]}
            onSubmit={(action) => action}
          />
        );
      } else if (enrollmentApplications?.length > 1) {
        openModal(<MultipleEnrollmentConfirmationModal applications={enrollmentApplications} />);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberTraits, approvedApplications.length]);

  return (
    <div className='home'>
      <ErrorBanner />
      {showConfetti && <Confetti />}
      <Banner />
      <LearningLifecycles />
      <div className='home-content'>
        <Layout />
      </div>
    </div>
  );
};

export default Home;
